import React, { useContext, useState } from 'react';
import {
  mergeClasses,
  ToggleButton,
  Tooltip,
} from '@fluentui/react-components';
import {
  PinFilled,
  PinRegular,
  Open20Regular,
} from '@fluentui/react-icons';

import { SelectionFlatTree } from '@bingads-webui-fluent-component/selection-tree';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Link } from '@bingads-webui-react/router';

import { I18n, TreeNode } from './types';
import { TextWithHighlight } from './text-with-highlight';
import { useListItemStyles } from './styles';

const renderTreeNode = (tool) => {
  const i18n: I18n = useContext(I18nContext);
  const classes = useListItemStyles();

  return (
    <Link
      className={mergeClasses('tools-list-item-action', classes.infoContainer)}
      aria-label={i18n.getString(_TL_('Open {{toolName}}'), { toolName: tool.name })}
      to={tool.route}
      onClick={tool.onClick}
      target={tool.isExternal ? '_blank' : undefined}
      rel={tool.isExternal ? 'noopener noreferrer' : undefined}
    >
      <div className={classes.titleContainer}>
        <TextWithHighlight
          className={tool.level === 1
            ? mergeClasses('title', classes.title, classes.link)
            : mergeClasses('subtitle', classes.subtitle, classes.link)}
          text={i18n.getString(tool.name)}
          hightlight={tool.highlight}
        />
        {tool.isExternal && (
          <Open20Regular className={classes.externalIcon} />
        )}
      </div>
      {tool.level === 1 && (
        <TextWithHighlight
          className={mergeClasses('description', classes.description)}
          text={i18n.getString(tool.description)}
          hightlight={tool.highlight}
        />
      )}
    </Link>
  );
};

export const ToolListItem: React.FC<{
  tool: TreeNode;
}> = ({ tool }) => {
  const i18n: I18n = useContext(I18nContext);
  const classes = useListItemStyles();
  const [openItems, setOpenItems] = useState(new Set<any>());

  const ariaLabel = (tool.isPinned
    ? i18n.getString(_TL_('{{toolName}} - currently pinned'), { toolName: tool.name })
    : i18n.getString(tool.name))
    + ' ' + i18n.getString(tool.description);

  return (
    <div
      id={`menu-tool-${tool.key}`}
      className={mergeClasses('tools-list-item', tool.key, classes.root)}
      aria-label={ariaLabel}
      tabIndex={0}
      role="menuitem"
    >
      <div className={classes.iconContainer}>
        {tool.isPinnable && (
        <Tooltip
          content={i18n.getString(tool.isPinned ? _TL_('Unpin {{toolName}}') : _TL_('Pin {{toolName}}'), { toolName: tool.name })}
          relationship="label"
          withArrow
        >
          <ToggleButton
            className={mergeClasses(
              'pin-toggle-button',
              tool.isPinned ? 'pinned' : 'unpinned',
              tool.isUnpinnable ? 'unpinnable' : 'pinable',
            )}
            checked={tool.isPinned}
            appearance="transparent"
            icon={tool.isPinned ? <PinFilled /> : <PinRegular />}
            onClick={tool.onTogglePin}
            disabled={!tool.isUnpinnable}
            size="small"
          />
        </Tooltip>
        )}
      </div>
      <SelectionFlatTree
        i18n={i18n}
        selectionMode="none"
        treeItems={tool.flatTree}
        itemsMap={tool.treeMap}
        openItems={openItems}
        onOpenChange={setOpenItems}
        onRenderContent={renderTreeNode}
        onChange={() => {}}
        treeClassName={classes.tree}
        aria-label={i18n.getString(_TL_('{{toolName}} actions'), { toolName: tool.name })}
      />
    </div>
  );
};
